import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Pagination } from '@material-ui/lab';
import { Autocomplete } from '@material-ui/lab';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as XLSX from 'xlsx-js-style';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { hasGlobalStations, scopeStations, allowedSerials } from '../components/roleAccess';
import './OffloadingReports.css';

import { API_BASE } from '../config';
import { tr } from '../i18n';
const MATCH_WINDOW_HOURS  = 11;
const PER_PAGE            = 20;

// The page opens on the last DEFAULT_DAYS days instead of the whole history,
// which is what made the first load slow. 0 = open with everything (old
// behaviour). Clearing the Start date and pressing Search still loads all.
const DEFAULT_DAYS        = 30;

// ATG detections below this many litres are ignored (level noise, small
// top-ups, not real deliveries). Checked AFTER fragments of one delivery are
// merged, so a delivery reported in pieces still counts by its total.
const MIN_ATG_VOLUME      = 1500;

// Parse once: "2026-06-13 11:52:45" and ISO strings, as local time.
const tsOf = (v) => new Date(String(v).replace(' ', 'T')).getTime();

function formatDate(iso) {
  if (!iso) return '-';
  return iso.replace('T', ' ').slice(0, 16);
}

// Date and time split for the table, which stacks them on two lines. The
// export keeps using formatDate so a spreadsheet cell stays one sortable
// value.
function splitDate(iso) {
  if (!iso) return { day: '-', time: '' };
  const clean = iso.replace('T', ' ');
  return { day: clean.slice(0, 10), time: clean.slice(11, 16) };
}

// 'YYYY-MM-DD' for a Date object (local), used for date <input> + query params
function toDateInput(d) {
  const z = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

// shift a 'YYYY-MM-DD' string by N days, returns 'YYYY-MM-DD'
function shiftDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return toDateInput(d);
}

const z2 = n => String(n).padStart(2, '0');

// VFD vs ATG-only switch. The backend has sent is_vfd as a real boolean, as
// 1/0 and as "1"/"true" depending on the endpoint, so normalise rather than
// testing truthiness - the string "0" is truthy in JS (same as Stations.js).
const isVfdStation = (s) => {
  const v = s && s.is_vfd;
  if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
  return v === 1 || v === true;
};
const typeOf = (s) => (isVfdStation(s) ? 'vfd' : 'atg');
const STATION_TYPE_KEY = 'offloading_station_type';

const EMPTY_REFILL = {
  tank_id:              '',
  supplier_name:        '',
  driver_name:          '',
  station_serial:       '',
  tanker_license_plate: '',
  issue_date_day:       '', // 'YYYY-MM-DD'
  issue_hour:           '',
  issue_minute:         '',
  issue_second:         '',
  fuel_amount:          '',
  liter_price:          '',
  product:              '',
  dip_start:            '',
  dip_end:              '',
};

// The station changed how it detects an offload on this date. The OLD
// algorithm reports a real duration_minutes; the NEW one always reports 0.
// Both ran for a while, so the same physical refill can appear twice.
const AUTOREFILL_ALGO_CUTOFF = '2026-08-01';

// How close two records must be to count as the same refill. The old
// algorithm timestamps at the END of the offload and the new one at
// detection, so the gap is roughly the length of a delivery.
const AUTOREFILL_MATCH_HOURS = 6;

/**
 * Pick one record per physical refill.
 *
 *   from the cutoff onward : only zero-duration records (the new algorithm)
 *   before the cutoff      : non-zero records, plus any zero-duration record
 *                            that has no non-zero twin — otherwise a refill
 *                            only the new algorithm caught would vanish
 *
 * A "twin" is the same tank at the same station within
 * AUTOREFILL_MATCH_HOURS.
 */
function selectAutorefills(autorefills) {
  const cutoff = new Date(`${AUTOREFILL_ALGO_CUTOFF}T00:00:00`).getTime();
  const windowMs = AUTOREFILL_MATCH_HOURS * 3600 * 1000;

  const isZero = (r) => !Number(r.duration_minutes);
  const keyOf = (r) => `${r.station_serial}|${r.tank_id}`;

  // Timestamps parsed once, and non-zero records grouped by station|tank, so
  // the twin check below looks only at the same tank instead of every record.
  const times = autorefills.map((r) => tsOf(r.created_at));
  const nonZeroTimes = {};
  autorefills.forEach((r, i) => {
    if (!isZero(r) && !Number.isNaN(times[i])) {
      const k = keyOf(r);
      (nonZeroTimes[k] = nonZeroTimes[k] || []).push(times[i]);
    }
  });

  return autorefills.filter((record, i) => {
    const when = times[i];
    const zero = isZero(record);

    // Records with an unreadable timestamp fall back to the old behaviour
    // rather than being silently dropped.
    if (Number.isNaN(when)) return !zero;

    if (when >= cutoff) return zero;

    if (!zero) return true;

    // Pre-cutoff zero-duration: keep it only if nothing non-zero covers it.
    const hasTwin = (nonZeroTimes[keyOf(record)] || []).some(
      (t) => Math.abs(t - when) <= windowMs
    );
    return !hasTwin;
  });
}

// A single delivery can be reported as several detections a few minutes
// apart — one tanker offloading in stages, or the ATG settling between
// readings. Each fragment then gets compared against the FULL order quantity,
// so a 17,000 L delivery seen as 10,776 + 6,240 shows two rows with a huge
// negative difference instead of one row that balances.
const AUTOREFILL_MERGE_MINUTES = 180;

/**
 * Combine fragments of the same delivery into one record.
 *
 * Fragments are consecutive records for the same tank at the same station,
 * each within AUTOREFILL_MERGE_MINUTES of the previous one. The chain keeps
 * extending, so three fragments an hour apart merge into one.
 *
 * Only zero-duration records are merged: those come from the station's newer
 * algorithm, which is the one that splits. The older algorithm tracks a
 * delivery over a real duration and already reports it whole, and leaving it
 * alone means historical reports do not shift under the client.
 */
function mergeAutorefills(autorefills) {
  const tsCache = new Map();
  const ts = (r) => {
    if (!tsCache.has(r)) tsCache.set(r, tsOf(r.created_at));
    return tsCache.get(r);
  };
  const isZero = (r) => !Number(r.duration_minutes);
  const windowMs = AUTOREFILL_MERGE_MINUTES * 60 * 1000;

  const mergeable = [];
  const untouched = [];
  autorefills.forEach((r) => {
    (isZero(r) && !Number.isNaN(ts(r)) ? mergeable : untouched).push(r);
  });

  const groups = {};
  mergeable.forEach((r) => {
    const key = `${r.station_serial}|${r.tank_id}`;
    (groups[key] = groups[key] || []).push(r);
  });

  const out = [...untouched];

  Object.values(groups).forEach((records) => {
    records.sort((a, b) => ts(a) - ts(b));

    let chain = [records[0]];
    const flush = () => {
      const last = chain[chain.length - 1];
      if (chain.length === 1) {
        out.push(last);
        return;
      }
      out.push({
        ...last,
        // The latest timestamp is when the delivery finished, which is what
        // the refill paperwork is matched against.
        created_at: last.created_at,
        start_volume: chain[0].start_volume,
        end_volume: last.end_volume,
        // Summed rather than end - start: fuel can be dispensed between
        // fragments, which would make the span overstate what was delivered.
        auto_delivery: chain.reduce((sum, r) => sum + Number(r.auto_delivery || 0), 0),
        merged_count: chain.length,
      });
    };

    for (let i = 1; i < records.length; i += 1) {
      const prev = chain[chain.length - 1];
      if (ts(records[i]) - ts(prev) <= windowMs) {
        chain.push(records[i]);
      } else {
        flush();
        chain = [records[i]];
      }
    }
    flush();
  });

  return out;
}

function matchRecords(refills, autorefills) {
  const windowMs = MATCH_WINDOW_HOURS * 60 * 60 * 1000;
  const keyOf = (serial, tank, product) => `${serial}|${tank}|${(product || '').toUpperCase()}`;

  // Refills grouped by station|tank|product with their time parsed once, in
  // the server's order - so "first unused refill in the window" picks the
  // same refill as before, but only searches that tank's refills.
  const groups = {};
  refills.forEach((r, idx) => {
    const k = keyOf(r.station_serial, r.tank_id, r.product);
    (groups[k] = groups[k] || []).push({ r, idx, t: tsOf(r.issue_date) });
  });
  const usedRefill = new Set();

  // autorefill is the base — iterate over ATG records
  const matched = autorefills.map(a => {
    // NOTE: no duration_minutes filter here any more. This used to skip every
    // zero-duration record, which hid ALL offloads produced by the station's
    // newer algorithm. selectAutorefills() decides which records survive.
    const aT = tsOf(a.created_at);

    // Matching refill: same tank_id, station_serial and product, within
    // MATCH_WINDOW_HOURS.
    const hit = (groups[keyOf(a.station_serial, a.tank_id, a.product)] || [])
      .find(c => !usedRefill.has(c.idx) && Math.abs(aT - c.t) <= windowMs);
    if (hit) usedRefill.add(hit.idx);
    const refillMatch = hit ? hit.r : null;

    const orderQty   = refillMatch ? refillMatch.fuel_amount   : null;
    const dipstick   = refillMatch ? Math.round((refillMatch.dip_end - refillMatch.dip_start) * 100) / 100 : null;
    const atgVol     = a.auto_delivery;
    const difference = orderQty != null ? Math.round((atgVol - orderQty) * 100) / 100 : null;

    return {
      date:           a.created_at,
      sort_ts:        aT,
      station_serial: a.station_serial,
      tank_id:        a.tank_id,
      product:        a.product,
      atg_volume:     atgVol,
      merged_count:   a.merged_count || 1,
      order_qty:      orderQty,
      dipstick:       dipstick,
      supplier:       refillMatch ? refillMatch.supplier_name        : null,
      driver:         refillMatch ? refillMatch.driver_name          : null,
      plate:          refillMatch ? refillMatch.tanker_license_plate : null,
      difference:     difference,
      refill_date:    refillMatch ? refillMatch.issue_date           : null,
    };
  });

  return matched.sort((a, b) => (b.sort_ts || 0) - (a.sort_ts || 0));
}

export default function OffloadingReports({ token }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [stationFilter, setStationFilter] = useState('');

  // Drill-down: clicking a station name in the All Stations view narrows to
  // that station. Page is reset because the current page number almost never
  // exists in the smaller result — staying on page 4 of a one-page result
  // shows an empty table and reads as "no records".
  const drillIntoStation = (serial) => {
    if (!serial || stationFilter) return;
    setStationFilter(serial);
    setPage(1);
  };
  const [stations, setStations]           = useState([]);
  const [page, setPage]                   = useState(1);

  // 'vfd' = only VFD stations, 'atg' = only ATG stations. Remembered for the
  // browser session so it survives moving between pages.
  const [stationType, setStationType] = useState(() => {
    try { return sessionStorage.getItem(STATION_TYPE_KEY) === 'atg' ? 'atg' : 'vfd'; } catch (e) { return 'vfd'; }
  });
  const [reloadTick, setReloadTick] = useState(0);

  // empty by default → page opens showing everything; dates are opt-in for
  // narrowing the view / scoping an export.
  const [sinceDate, setSinceDate] = useState(() =>
    DEFAULT_DAYS ? shiftDays(toDateInput(new Date()), -DEFAULT_DAYS) : '');
  const [toDate, setToDate]       = useState('');

  // Manual "Add Refill" modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRefill, setNewRefill]       = useState(EMPTY_REFILL);
  const [addError, setAddError]         = useState('');
  const [addSaving, setAddSaving]       = useState(false);

  // Global roles see every station; Managers/Cluster Managers are scoped to
  // their associated station(s). See components/roleAccess.
  const isGlobal = hasGlobalStations(token);
  const allowed  = useMemo(() => allowedSerials(token), [token]); // null = all (global)

  const accessToken = token?.data?.access_token || token?.access_token;
  const headers     = { Authorization: accessToken };

  useEffect(() => {
    fetch(`${API_BASE}/station/all`, { headers })
      .then(r => r.json())
      .then(d => {
        const scoped = scopeStations(token, d.data || []);
        setStations(scoped);
        if (!isGlobal && scoped.length === 1) setStationFilter(scoped[0].serial_number);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STATION_TYPE_KEY, stationType); } catch (e) { /* ignore */ }
  }, [stationType]);

  // serial -> station, built once per station list (used for names and the
  // VFD/ATG split instead of searching the list for every row).
  const stationMap = useMemo(() => {
    const m = {};
    stations.forEach(s => { m[s.serial_number] = s; });
    return m;
  }, [stations]);
  const nameOf = (serial) => (stationMap[serial] && stationMap[serial].name) || serial || '-';

  const vfdCount     = stations.filter(isVfdStation).length;
  const atgCount     = stations.length - vfdCount;
  const showTypeSwitch = vfdCount > 0 && atgCount > 0;
  const typeStations = stations.filter(s => typeOf(s) === stationType);

  // A user whose stations are all one type (e.g. a Manager of one ATG
  // station) gets that type automatically, and no switch.
  useEffect(() => {
    if (!stations.length) return;
    if (stationType === 'vfd' && vfdCount === 0 && atgCount > 0) setStationType('atg');
    if (stationType === 'atg' && atgCount === 0 && vfdCount > 0) setStationType('vfd');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations]);

  const chooseType = (type) => {
    if (type === stationType) return;
    setStationType(type);
    setPage(1);
    // A selected station of the other type no longer fits: clear it, and
    // reload, because the last search may have asked the server for that
    // one station only.
    const selected = stations.find(s => s.serial_number === stationFilter);
    if (selected && typeOf(selected) !== type) {
      setStationFilter('');
      setReloadTick(t => t + 1);
    }
  };

  const loadData = useCallback(() => {
    if (sinceDate && toDate && sinceDate > toDate) {
      setError(tr("Start date must be on or before end date."));
      return;
    }
    setLoading(true);
    setError('');

    // Server filters autorefill (the base) to the exact range + station.
    // Refills are padded ±1 day so a refill that matches an ATG record across
    // a midnight boundary (within the 5h window) is still fetched.
    const autoParams   = new URLSearchParams();
    const refillParams = new URLSearchParams();
    if (sinceDate) {
      autoParams.set('since_date',   sinceDate);
      refillParams.set('since_date', shiftDays(sinceDate, -1));
    }
    if (toDate) {
      autoParams.set('to_date',   toDate);
      refillParams.set('to_date', shiftDays(toDate, 1));
    }
    // Scope to one station server-side when one is selected. (The server does
    // not reliably scope by token, so we also client-filter below.)
    if (stationFilter) {
      autoParams.set('station_serial',   stationFilter);
      refillParams.set('station_serial', stationFilter);
    }

    // Station list is loaded once on mount (names are looked up at render),
    // so Search only fetches the two record lists.
    Promise.all([
      fetch(`${API_BASE}/ak4tek/tanks/refill?${refillParams.toString()}`,   { headers }).then(r => r.json()),
      fetch(`${API_BASE}/ak4tek/tanks/autorefill?${autoParams.toString()}`, { headers }).then(r => r.json()),
    ])
      .then(([refillRes, autoRes]) => {
        // An API error used to be swallowed and shown as "No records found".
        [['Refill', refillRes], ['ATG', autoRes]].forEach(([name, res]) => {
          if (!res || (res.data === undefined && res.message)) {
            throw new Error(`${name}: ${(res && res.message) || 'empty response'}`);
          }
        });
        const refills     = refillRes?.data?.records   || refillRes?.data  || [];
        const autorefills = autoRes?.data?.records     || autoRes?.data    || [];
        const deliveries = mergeAutorefills(selectAutorefills(autorefills))
          .filter(a => Number(a.auto_delivery || 0) >= MIN_ATG_VOLUME);
        setRows(matchRecords(refills, deliveries));
        setPage(1);
      })
      .catch(e => setError(tr("Failed to load data: {0}", e.message)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceDate, toDate, stationFilter, isGlobal, accessToken]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reloadTick) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  // Client-side filter = correctness (works even if the backend ignores the
  // query params); the server-side params are a payload-size optimisation.
  // Date compare uses the YYYY-MM-DD prefix, which is correct for both the
  // "2026-06-13 11:52:45" and ISO "2026-06-13T...Z" stored formats.
  const filtered = useMemo(() => rows.filter(r => {
    // Hard scope: a non-global user never sees rows outside their stations,
    // even if the server returned them.
    if (allowed && !allowed.includes(r.station_serial)) return false;
    // VFD / ATG switch. A row whose station is not in the station list can't
    // be classified, so it stays visible in both views rather than vanishing.
    const st = stationMap[r.station_serial];
    if (st && typeOf(st) !== stationType) return false;
    if (stationFilter && r.station_serial !== stationFilter) return false;
    const day = (r.date || '').slice(0, 10);
    if (sinceDate && day < sinceDate) return false;
    if (toDate   && day > toDate)     return false;
    return true;
  }), [rows, allowed, stationMap, stationType, stationFilter, sinceDate, toDate]);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function exportExcel() {
    if (!filtered.length) return;

    const header = [
      '#', tr('Date'), tr('Station'), tr('Tank'), tr('Product'),
      tr('Supplier'), tr('Driver'), tr('Plate'),
      tr('ATG Volume'), tr('Order QTY'), tr('Dipstick Vol'), tr('Difference (ATG - Order)'),
    ];

    const aoa = [header];
    filtered.forEach((row, i) => {
      aoa.push([
        i + 1,
        formatDate(row.date),
        nameOf(row.station_serial),
        row.tank_id,
        row.product || '-',
        row.supplier || '-',
        row.driver   || '-',
        row.plate    || '-',
        row.atg_volume != null ? row.atg_volume : '',
        row.order_qty  != null ? row.order_qty  : '',
        row.dipstick   != null ? row.dipstick   : '',
        row.difference != null ? row.difference : '',
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // bold header row
    for (let c = 0; c < header.length; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '343A40' } },
          alignment: { horizontal: 'center' },
        };
      }
    }
    ws['!cols'] = [
      { wch: 5 }, { wch: 18 }, { wch: 24 }, { wch: 6 }, { wch: 12 },
      { wch: 20 }, { wch: 18 }, { wch: 14 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 18 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Offloading');

    const scope = stationFilter
      ? (stations.find(s => s.serial_number === stationFilter)?.name || stationFilter)
      : `${isGlobal ? 'All' : 'My'}_${stationType === 'vfd' ? 'VFD' : 'ATG'}_Stations`;
    const safe  = String(scope).replace(/[^a-zA-Z0-9]+/g, '_');
    const range = (sinceDate || toDate)
      ? `${sinceDate || 'start'}_to_${toDate || 'end'}`
      : 'all';
    XLSX.writeFile(wb, `offloading_report_${safe}_${range}.xlsx`);
  }

  function openAddModal() {
    setNewRefill({ ...EMPTY_REFILL, station_serial: stationFilter || '' });
    setAddError('');
    setShowAddModal(true);
  }

  function closeAddModal() {
    setShowAddModal(false);
    setNewRefill(EMPTY_REFILL);
    setAddError('');
  }

  function handleNewRefillChange(field, value) {
    setNewRefill(prev => ({ ...prev, [field]: value }));
  }

  // Clamp a typed hour/minute/second value into a valid 24h range as the
  // user types, so it can never become e.g. "25" or "-3".
  function handleTimePartChange(field, max, rawValue) {
    if (rawValue === '') { handleNewRefillChange(field, ''); return; }
    let n = parseInt(rawValue, 10);
    if (isNaN(n)) return;
    if (n < 0) n = 0;
    if (n > max) n = max;
    handleNewRefillChange(field, String(n));
  }

  function submitNewRefill() {
    // Only these are still mandatory - supplier/driver/plate/liter_price
    // are optional and sent as null when left blank.
    const required = ['tank_id', 'station_serial', 'issue_date_day', 'issue_hour',
                       'issue_minute', 'issue_second', 'fuel_amount', 'product',
                       'dip_start', 'dip_end'];
    const missing = required.filter(f => newRefill[f] === '' || newRefill[f] == null);
    if (missing.length) {
      setAddError(tr("Missing: {0}", missing.join(', ')));
      return;
    }

    setAddSaving(true);
    setAddError('');

    const issueDate = `${newRefill.issue_date_day} ${z2(newRefill.issue_hour)}:${z2(newRefill.issue_minute)}:${z2(newRefill.issue_second)}`;

    const optionalText = v => (v === '' || v == null ? '' : v);
    const optionalNum  = v => (v === '' || v == null ? 0 : Number(v));

    const payload = {
      tank_id:              Number(newRefill.tank_id),
      supplier_name:        optionalText(newRefill.supplier_name),
      driver_name:          optionalText(newRefill.driver_name),
      station_serial:       newRefill.station_serial,
      tanker_license_plate: optionalText(newRefill.tanker_license_plate),
      issue_date:           issueDate,
      fuel_amount:          Number(newRefill.fuel_amount),
      liter_price:          optionalNum(newRefill.liter_price),
      product:              newRefill.product,
      dip_start:            Number(newRefill.dip_start),
      dip_end:              Number(newRefill.dip_end),
      // Not entered manually here - no ATG probe reading for a manual
      // entry. Sent as 0, not null, since the backend's require_keys
      // rejects null values outright.
      start_fuel_level_mm:  0,
      fuel_volume:          0,
    };

    fetch(`${API_BASE}/ak4tek/tanks/refill`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(body?.message || JSON.stringify(body) || `Request failed (${r.status})`);
        }
        return body;
      })
      .then(() => {
        closeAddModal();
        loadData();
        setAddSaving(false);
        Swal.fire({
          title: tr("Success!"),
          text: tr("Refill added successfully."),
          icon: 'success',
          confirmButtonText: tr("Close"),
          confirmButtonColor: '#A5DC86',
        });
      })
      .catch(e => {
        setAddError(e.message);
        setAddSaving(false);
      });
  }

  return (
    <div className="OffloadingPage">
      <div className="FilterBar">
        <h5 className="FilterBarTitle">{tr("Offloading Reports")}</h5>
        {showTypeSwitch && (
          <div style={{ marginBottom: 10 }}>
            <div className="btn-group" role="group" aria-label={tr("Station type")}>
              <button
                type="button"
                className={`btn btn-sm ${stationType === 'vfd' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => chooseType('vfd')}
                aria-pressed={stationType === 'vfd'}
              >
                <i className="fas fa-microchip"></i> {tr("VFD Stations")} ({vfdCount})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${stationType === 'atg' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => chooseType('atg')}
                aria-pressed={stationType === 'atg'}
              >
                <i className="fas fa-gas-pump"></i> {tr("ATG Only")} ({atgCount})
              </button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {(isGlobal || stations.length > 1) && (
            <FormControl style={{ minWidth: 240 }}>
              <Autocomplete
                options={typeStations}
                getOptionLabel={s => s.name || ''}
                value={typeStations.find(s => s.serial_number === stationFilter) || null}
                onChange={(e, newValue) => setStationFilter(newValue ? newValue.serial_number : '')}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={stationType === 'vfd'
                      ? (isGlobal ? tr("All VFD Stations") : tr("All My VFD Stations"))
                      : (isGlobal ? tr("All ATG Stations") : tr("All My ATG Stations"))}
                  />
                )}
              />
              <FormHelperText>{tr("Station")}</FormHelperText>
            </FormControl>
          )}

          <FormControl>
            <input
              type="date"
              value={sinceDate}
              max={toDate || undefined}
              onChange={e => setSinceDate(e.target.value)}
              className="form-control"
              style={{ minWidth: 150 }}
            />
            <FormHelperText>{tr("Start date")}</FormHelperText>
          </FormControl>

          <FormControl>
            <input
              type="date"
              value={toDate}
              min={sinceDate || undefined}
              onChange={e => setToDate(e.target.value)}
              className="form-control"
              style={{ minWidth: 150 }}
            />
            <FormHelperText>{tr("End date")}</FormHelperText>
          </FormControl>

          <button
            className="btn btn-primary"
            onClick={loadData}
            disabled={loading}
          >
            {loading ? tr("Loading…") : tr("Search")}
          </button>

          <button
            className="btn btn-success"
            onClick={exportExcel}
            disabled={loading || filtered.length === 0}
          >
            {tr("Export to Excel")}
          </button>

          {/* Temporarily hidden — re-enable by removing this comment wrapper.
              The modal, openAddModal/closeAddModal and the submit handler are
              all left intact, so nothing else has to change to bring it back.
          <button
            className="btn btn-secondary"
            onClick={openAddModal}
          >
            + Add Refill
          </button>
          */}

          <span style={{ color: '#666', fontSize: 13 }}>
            {tr("{0} record(s) — Page {1} of {2}", filtered.length, page, totalPages || 1)}
          </span>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12 }}>
          <CircularProgress style={{ color: '#e50914' }} />
          <span style={{ color: '#888', fontSize: 14 }}>{tr("Loading...")}</span>
        </div>
      )}
      {error   && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

      {!loading && !error && (
        <div style={{ overflowX: 'auto' }}>
          <table
            className='table table-striped table-bordered table-hover'
            style={{ fontSize: 13, minWidth: 1200 }}
          >
            <thead className='thead-dark'>
              <tr>
                <th>#</th>
                <th className="col-date">{tr("Date")}</th>
                {(isGlobal || stations.length > 1) && <th>{tr("Station")}</th>}
                <th>{tr("Tank")}</th>
                <th className="col-product">{tr("Product")}</th>
                <th>{tr("Supplier")}</th>
                <th>{tr("Driver")}</th>
                <th>{tr("Plate")}</th>
                <th>{tr("ATG Volume")}</th>
                <th>{tr("Order QTY")}</th>
                <th>{tr("Dipstick Vol")}</th>
                <th>{tr("Difference (ATG - Order)")}</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((row, i) => {
                // Traffic-light band on the ATG-vs-Order difference.
                // The colour lives in OffloadingReports.css (diff-ok /
                // diff-warn / diff-bad) rather than an inline style, so the
                // theme controls it — an inline background cannot be
                // overridden by a stylesheet, and the pale pastels this used
                // to set were unreadable on a dark table.
                const diffClass =
                  row.difference == null ? '' :
                  Math.abs(row.difference) < 200  ? 'diff-ok'   :
                  Math.abs(row.difference) < 1000 ? 'diff-warn' : 'diff-bad';
                return (
                  <tr key={i}>
                    <td>{(page - 1) * PER_PAGE + i + 1}</td>
                    <td className="col-date">
                      <span className="date-day">{splitDate(row.date).day}</span>
                      <span className="date-time">{splitDate(row.date).time}</span>
                    </td>
                    {(isGlobal || stations.length > 1) && (
                      <td>
                        {stationFilter ? (
                          nameOf(row.station_serial)
                        ) : (
                          <button
                            type="button"
                            className="station-drill"
                            onClick={() => drillIntoStation(row.station_serial)}
                            title={tr("Show only {0}", nameOf(row.station_serial))}
                          >
                            {nameOf(row.station_serial)}
                          </button>
                        )}
                      </td>
                    )}
                    <td>{row.tank_id}</td>
                    <td className="col-product">{row.product}</td>
                    <td>{row.supplier || '-'}</td>
                    <td>{row.driver   || '-'}</td>
                    <td>{row.plate    || '-'}</td>
                    <td
                      title={
                        row.merged_count > 1
                          ? tr("Combined from {0} ATG detections of the same delivery", row.merged_count)
                          : undefined
                      }
                    >
                      <b>{row.atg_volume?.toLocaleString()}</b>
                    </td>
                    <td>{row.order_qty != null ? row.order_qty.toLocaleString() : <span style={{ color: '#bbb' }}>{tr("No Refill")}</span>}</td>
                    <td>{row.dipstick != null ? row.dipstick.toLocaleString() : '-'}</td>
                    <td className={diffClass}>
                      {row.difference != null ? row.difference.toLocaleString() : '-'}
                    </td>
                  </tr>
                );
              })}
              {paginated.length === 0 && (
                <tr><td colSpan={(isGlobal || stations.length > 1) ? 12 : 11} style={{ textAlign: 'center', color: '#888' }}>{tr("No records found")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="ReportPagination">
        <Pagination
          count={totalPages || 1}
          page={page}
          onChange={(event, newPage) => setPage(newPage)}
          variant="outlined"
          color="primary"
        />
      </div>

      <Dialog open={showAddModal} onClose={closeAddModal} maxWidth="sm" fullWidth>
        <DialogTitle>{tr("Add Refill (Manual)")}</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
            {(isGlobal || stations.length > 1) ? (
              <Autocomplete
                options={stations}
                getOptionLabel={s => s.name || ''}
                value={stations.find(s => s.serial_number === newRefill.station_serial) || null}
                onChange={(e, v) => handleNewRefillChange('station_serial', v ? v.serial_number : '')}
                renderInput={params => <TextField {...params} label={tr("Station")} required />}
              />
            ) : (
              <TextField
                label={tr("Station Serial")}
                value={newRefill.station_serial}
                onChange={e => handleNewRefillChange('station_serial', e.target.value)}
                required
              />
            )}

            <TextField
              label={tr("Tank ID")}
              type="number"
              value={newRefill.tank_id}
              onChange={e => handleNewRefillChange('tank_id', e.target.value)}
              required
            />

            <TextField
              label={tr("Product")}
              value={newRefill.product}
              onChange={e => handleNewRefillChange('product', e.target.value)}
              placeholder={tr("Diesel / Unleaded")}
              required
            />

            <TextField
              label={tr("Supplier Name")}
              value={newRefill.supplier_name}
              onChange={e => handleNewRefillChange('supplier_name', e.target.value)}
            />

            <TextField
              label={tr("Driver Name")}
              value={newRefill.driver_name}
              onChange={e => handleNewRefillChange('driver_name', e.target.value)}
            />

            <TextField
              label={tr("Tanker License Plate")}
              value={newRefill.tanker_license_plate}
              onChange={e => handleNewRefillChange('tanker_license_plate', e.target.value)}
            />

            {/* Guaranteed 24-hour entry: a plain date field (always
                YYYY-MM-DD, locale-independent) plus three separate
                Hour/Minute/Second number inputs capped to valid ranges.
                No native time control is used, so there's no AM/PM
                rendering possible regardless of browser/OS locale. */}
            <div>
              <TextField
                label={tr("Issue Date")}
                type="date"
                value={newRefill.issue_date_day}
                onChange={e => handleNewRefillChange('issue_date_day', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <TextField
                  label={tr("Hour (0-23)")}
                  type="number"
                  value={newRefill.issue_hour}
                  onChange={e => handleTimePartChange('issue_hour', 23, e.target.value)}
                  inputProps={{ min: 0, max: 23 }}
                  required
                />
                <TextField
                  label={tr("Minute")}
                  type="number"
                  value={newRefill.issue_minute}
                  onChange={e => handleTimePartChange('issue_minute', 59, e.target.value)}
                  inputProps={{ min: 0, max: 59 }}
                  required
                />
                <TextField
                  label={tr("Second")}
                  type="number"
                  value={newRefill.issue_second}
                  onChange={e => handleTimePartChange('issue_second', 59, e.target.value)}
                  inputProps={{ min: 0, max: 59 }}
                  required
                />
              </div>
            </div>

            <TextField
              label={tr("Fuel Amount (Ordered)")}
              type="number"
              value={newRefill.fuel_amount}
              onChange={e => handleNewRefillChange('fuel_amount', e.target.value)}
              required
            />

            <TextField
              label={tr("Liter Price")}
              type="number"
              value={newRefill.liter_price}
              onChange={e => handleNewRefillChange('liter_price', e.target.value)}
            />

            <TextField
              label={tr("Dip Start")}
              type="number"
              value={newRefill.dip_start}
              onChange={e => handleNewRefillChange('dip_start', e.target.value)}
              required
            />

            <TextField
              label={tr("Dip End")}
              type="number"
              value={newRefill.dip_end}
              onChange={e => handleNewRefillChange('dip_end', e.target.value)}
              required
            />

            {addError && <p style={{ color: 'red', margin: 0 }}>{addError}</p>}
          </div>
        </DialogContent>
        <DialogActions>
          <button className="btn btn-secondary" onClick={closeAddModal} disabled={addSaving}>
            {tr("Cancel")}
          </button>
          <button className="btn btn-primary" onClick={submitNewRefill} disabled={addSaving}>
            {addSaving ? tr("Saving…") : tr("Save")}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}